import { useState, useMemo } from 'react'
import { useToast } from '../../context/ToastContext'
import { getCategories, updateCategory, deleteCategory, UNIT_OPTIONS, CATEGORY_TYPES } from '../../data/categories'
import { BAKERY_CATEGORIES_SEED } from '../../data/bakeryData'
import { YAWEHS_CATEGORIES_SEED } from '../../data/yawehsData'
import { TUCKSHOP_CATEGORIES_SEED } from '../../data/tuckshopData'
import { Plus, Pencil, Save, Trash2, X, Check } from 'lucide-react'
import AddCategoryModal from '../ingestion/AddCategoryModal'

function getCategoriesForDept(dept) {
  if (dept === 'bakery') return BAKERY_CATEGORIES_SEED
  if (dept === 'yawehs') return YAWEHS_CATEGORIES_SEED
  if (dept === 'tuckshop') return TUCKSHOP_CATEGORIES_SEED
  return getCategories()
}

export default function CategoriesTab({ department }) {
  const { addToast } = useToast()
  const [refreshKey, setRefreshKey] = useState(0)
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [editingLabel, setEditingLabel] = useState(null)
  const [editLabelValue, setEditLabelValue] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)

  const categories = useMemo(() => getCategoriesForDept(department), [refreshKey, department])

  const handleUnitChange = (catName, field, value) => {
    updateCategory(catName, { [field]: value })
    setRefreshKey(k => k + 1)
    addToast(`Updated ${field} for ${catName}`, 'success')
  }

  const handleTypeChange = (catName, value) => {
    updateCategory(catName, { type: value })
    setRefreshKey(k => k + 1)
    addToast(`Updated type for ${catName}`, 'success')
  }

  const handleProjectableToggle = (catName, current) => {
    updateCategory(catName, { projectable: !current })
    setRefreshKey(k => k + 1)
  }

  const startEditLabel = (c) => {
    setEditingLabel(c.name)
    setEditLabelValue(c.label)
  }

  const saveLabel = (catName) => {
    if (editLabelValue.trim()) {
      updateCategory(catName, { label: editLabelValue.trim() })
      setRefreshKey(k => k + 1)
      addToast(`Renamed to "${editLabelValue.trim()}"`, 'success')
    }
    setEditingLabel(null)
  }

  const handleDelete = (name, label) => {
    deleteCategory(name)
    setConfirmDelete(null)
    setRefreshKey(k => k + 1)
    addToast(`Deleted category "${label}"`, 'success')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Click units, type, or label to edit inline. Use trash icon to delete.</p>
        <button
          onClick={() => setShowAddCategory(true)}
          className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Plus size={14} /> Add Meat Type
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 text-xs border-b border-gray-800">
              <th className="text-left py-2 px-2">Category</th>
              <th className="text-left py-2 px-2">Type</th>
              <th className="text-left py-2 px-2">Opening Unit</th>
              <th className="text-left py-2 px-2">Surplus Unit</th>
              <th className="text-left py-2 px-2">Sales Unit</th>
              <th className="text-center py-2 px-2">Projectable</th>
              <th className="text-left py-2 px-2">Source</th>
              <th className="text-center py-2 px-2 w-16">Delete</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(c => (
              <tr key={c.name} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                <td className="py-2 px-2 capitalize font-medium">
                  {editingLabel === c.name ? (
                    <div className="flex items-center gap-1">
                      <input value={editLabelValue} onChange={e => setEditLabelValue(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && saveLabel(c.name)}
                        className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white w-32"
                        autoFocus />
                      <button onClick={() => saveLabel(c.name)} className="text-green-400 hover:text-green-300"><Save size={12} /></button>
                      <button onClick={() => setEditingLabel(null)} className="text-gray-500 hover:text-gray-300"><X size={12} /></button>
                    </div>
                  ) : (
                    <span className="cursor-pointer hover:text-blue-400" onClick={() => startEditLabel(c)} title="Click to edit label">
                      {c.label} <Pencil size={10} className="inline text-gray-600 ml-1" />
                    </span>
                  )}
                </td>
                <td className="py-2 px-2">
                  <select
                    value={c.type}
                    onChange={e => handleTypeChange(c.name, e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-xs text-gray-300"
                  >
                    {CATEGORY_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 px-2">
                  <select
                    value={c.openingUnit}
                    onChange={e => handleUnitChange(c.name, 'openingUnit', e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-xs text-gray-300"
                  >
                    {UNIT_OPTIONS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 px-2">
                  <select
                    value={c.surplusUnit}
                    onChange={e => handleUnitChange(c.name, 'surplusUnit', e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-xs text-gray-300"
                  >
                    {UNIT_OPTIONS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 px-2">
                  <select
                    value={c.salesUnit}
                    onChange={e => handleUnitChange(c.name, 'salesUnit', e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-xs text-gray-300"
                  >
                    {UNIT_OPTIONS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 px-2 text-center">
                  <button onClick={() => handleProjectableToggle(c.name, c.projectable)}>
                    {c.projectable ? <Check size={14} className="text-green-400 inline" /> : <span className="text-gray-600">—</span>}
                  </button>
                </td>
                <td className="py-2 px-2">
                  {c.custom
                    ? <span className="text-xs bg-brand-900 text-brand-400 px-1.5 py-0.5 rounded">Custom</span>
                    : <span className="text-xs bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">Seed</span>
                  }
                </td>
                <td className="py-2 px-2 text-center">
                  {confirmDelete === c.name ? (
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleDelete(c.name, c.label)} className="text-red-400 hover:text-red-300 text-xs">Yes</button>
                      <button onClick={() => setConfirmDelete(null)} className="text-gray-500 hover:text-gray-300 text-xs">No</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmDelete(c.name)} className="text-gray-500 hover:text-red-400" title="Delete">
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddCategory && (
        <AddCategoryModal
          onClose={() => setShowAddCategory(false)}
          onCategoryAdded={() => setRefreshKey(k => k + 1)}
        />
      )}
    </div>
  )
}
